import { ReactElement, useEffect, useState } from "react";
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Radio,
  RadioGroup,
  FormControlLabel,
  IconButton,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import {
  BillingPeriod,
  Currency,
  PlanInput,
  PlanType,
  PricingPlanDto,
  ScopeOverride,
} from "../../../../data/clients/ExternalApiClient";

interface Props {
  initial: PricingPlanDto | null;
  availableScopes: string[];
  onSave: (input: PlanInput) => Promise<void>;
}

const CURRENCIES: Currency[] = ["DKK", "EUR", "USD", "GBP"];
const PERIODS: BillingPeriod[] = ["Weekly", "Monthly", "Quarterly", "Yearly"];

export function PlanEditor({
  initial,
  availableScopes,
  onSave,
}: Props): ReactElement {
  const [planType, setPlanType] = useState<PlanType>(
    initial?.planType ?? "FreeTierCap",
  );
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>(
    (initial?.billingPeriod as BillingPeriod) ?? "Monthly",
  );
  const [baseFee, setBaseFee] = useState<string>(
    initial?.baseFee != null ? String(initial.baseFee) : "",
  );
  const [includedCalls, setIncludedCalls] = useState<string>(
    initial?.includedCalls != null ? String(initial.includedCalls) : "1000",
  );
  const [overageRate, setOverageRate] = useState<string>(
    initial?.overageRate != null ? String(initial.overageRate) : "",
  );
  const [currency, setCurrency] = useState<Currency>(
    (initial?.currency as Currency) ?? "DKK",
  );
  const [notes, setNotes] = useState<string>(initial?.notes ?? "");
  const [overrides, setOverrides] = useState<ScopeOverride[]>(
    initial?.scopeOverrides ?? [],
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (!initial) return;
    setPlanType(initial.planType);
    setBillingPeriod(
      (initial.billingPeriod as BillingPeriod) ?? "Monthly",
    );
    setBaseFee(initial.baseFee != null ? String(initial.baseFee) : "");
    setIncludedCalls(
      initial.includedCalls != null ? String(initial.includedCalls) : "",
    );
    setOverageRate(
      initial.overageRate != null ? String(initial.overageRate) : "",
    );
    setCurrency((initial.currency as Currency) ?? "DKK");
    setNotes(initial.notes ?? "");
    setOverrides(initial.scopeOverrides);
  }, [initial]);

  async function handleSave() {
    setBusy(true);
    setError(null);
    try {
      const input: PlanInput = {
        planType,
        currency,
        notes: notes || undefined,
        scopeOverrides: overrides,
      };
      if (planType === "FreeTierCap") {
        input.includedCalls = Number(includedCalls);
      } else if (planType === "FlatPerCall") {
        input.overageRate = Number(overageRate);
      } else {
        input.billingPeriod = billingPeriod;
        input.baseFee = Number(baseFee);
        input.includedCalls = Number(includedCalls);
        input.overageRate = Number(overageRate);
      }
      await onSave(input);
      setSavedAt(Date.now());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  function addOverride() {
    setOverrides((prev) => [...prev, { scope: "", overageRate: 0 }]);
  }

  function updateOverride(index: number, patch: Partial<ScopeOverride>) {
    setOverrides((prev) =>
      prev.map((o, i) => (i === index ? { ...o, ...patch } : o)),
    );
  }

  function removeOverride(index: number) {
    setOverrides((prev) => prev.filter((_, i) => i !== index));
  }

  return (
    <div className="bg-white border border-slate-200 rounded-sm">
      <div className="px-5 py-4 border-b border-slate-200">
        <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold">
          Prisplan
        </div>
        <div className="text-sm text-slate-500 mt-1">
          Én plan per nøgle. Ændringer gælder den nuværende og fremtidige perioder.
        </div>
      </div>

      <div className="px-5 py-5 space-y-5">
        <RadioGroup
          row
          value={planType}
          onChange={(e) => setPlanType(e.target.value as PlanType)}
          className="!gap-2"
        >
          {(
            [
              { value: "FreeTierCap", label: "Gratis", sub: "Hård grænse" },
              { value: "FlatPerCall", label: "Pris per kald", sub: "Pris per request" },
              {
                value: "MonthlyWithOverage",
                label: "Abonnement + overforbrug",
                sub: "Grundgebyr + inkluderet + ekstra",
              },
            ] as { value: PlanType; label: string; sub: string }[]
          ).map((option) => {
            const selected = planType === option.value;
            return (
              <label
                key={option.value}
                className={`flex-1 px-4 py-3 border rounded-sm cursor-pointer transition-colors ${
                  selected
                    ? "border-slate-900 bg-slate-50"
                    : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <FormControlLabel
                  control={<Radio size="small" sx={{ mr: 0.5, p: 0.5 }} />}
                  value={option.value}
                  label={
                    <span>
                      <span
                        className={`text-sm font-medium ${selected ? "text-slate-900" : "text-slate-700"}`}
                      >
                        {option.label}
                      </span>
                      <span className="block text-[11px] text-slate-500 mt-0.5">
                        {option.sub}
                      </span>
                    </span>
                  }
                />
              </label>
            );
          })}
        </RadioGroup>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <FormControl size="small" fullWidth>
            <InputLabel>Valuta</InputLabel>
            <Select
              label="Valuta"
              value={currency}
              onChange={(e) => setCurrency(e.target.value as Currency)}
            >
              {CURRENCIES.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {planType === "MonthlyWithOverage" && (
            <FormControl size="small" fullWidth>
              <InputLabel>Faktureringsperiode</InputLabel>
              <Select
                label="Faktureringsperiode"
                value={billingPeriod}
                onChange={(e) =>
                  setBillingPeriod(e.target.value as BillingPeriod)
                }
              >
                {PERIODS.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p === "Weekly"
                      ? "Ugentligt"
                      : p === "Monthly"
                        ? "Månedligt"
                        : p === "Quarterly"
                          ? "Kvartalsvis"
                          : "Årligt"}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {(planType === "FreeTierCap" ||
            planType === "MonthlyWithOverage") && (
            <TextField
              size="small"
              label={
                planType === "FreeTierCap" ? "Samlet grænse" : "Inkluderede kald"
              }
              value={includedCalls}
              onChange={(e) =>
                setIncludedCalls(e.target.value.replace(/[^\d]/g, ""))
              }
              InputProps={{
                sx: { fontFamily: "ui-monospace, SFMono-Regular, monospace" },
              }}
            />
          )}

          {planType === "MonthlyWithOverage" && (
            <TextField
              size="small"
              label={`Grundgebyr (${currency})`}
              value={baseFee}
              onChange={(e) =>
                setBaseFee(e.target.value.replace(/[^\d.]/g, ""))
              }
              InputProps={{
                sx: { fontFamily: "ui-monospace, SFMono-Regular, monospace" },
              }}
            />
          )}

          {(planType === "FlatPerCall" ||
            planType === "MonthlyWithOverage") && (
            <TextField
              size="small"
              label={`Pris per kald (${currency})`}
              value={overageRate}
              onChange={(e) =>
                setOverageRate(e.target.value.replace(/[^\d.]/g, ""))
              }
              InputProps={{
                sx: { fontFamily: "ui-monospace, SFMono-Regular, monospace" },
              }}
            />
          )}
        </div>

        {planType !== "FreeTierCap" && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold">
                  Per-scope prisjusteringer
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Justér prisen per kald for specifikke scopes. Kald uden for
                  disse scopes bruger standardprisen.
                </div>
              </div>
              <button
                onClick={addOverride}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium text-slate-700 hover:text-slate-900 border border-slate-200 hover:border-slate-300 rounded-sm transition-colors"
              >
                <FontAwesomeIcon icon={faPlus} className="text-[9px]" />
                Tilføj override
              </button>
            </div>
            <div className="border border-slate-200 rounded-sm divide-y divide-slate-100">
              {overrides.length === 0 && (
                <div className="px-3 py-4 text-xs text-slate-400 italic text-center">
                  Ingen overrides — alle scopes faktureres med {overageRate || "0"}{" "}
                  {currency} per kald.
                </div>
              )}
              {overrides.map((ov, i) => (
                <div
                  key={i}
                  className="grid grid-cols-[1fr_140px_36px] items-center gap-3 px-3 py-2"
                >
                  <FormControl size="small" fullWidth>
                    <Select
                      value={ov.scope}
                      onChange={(e) =>
                        updateOverride(i, { scope: e.target.value })
                      }
                      displayEmpty
                      sx={{ fontFamily: "ui-monospace, SFMono-Regular, monospace", fontSize: 12 }}
                    >
                      <MenuItem value="" disabled>
                        Vælg scope
                      </MenuItem>
                      {availableScopes.map((s) => (
                        <MenuItem key={s} value={s}>
                          {s}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    value={String(ov.overageRate)}
                    onChange={(e) =>
                      updateOverride(i, {
                        overageRate: Number(e.target.value.replace(/[^\d.]/g, "")) || 0,
                      })
                    }
                    InputProps={{
                      sx: { fontFamily: "ui-monospace, SFMono-Regular, monospace" },
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => removeOverride(i)}
                    sx={{ color: "rgb(100 116 139)" }}
                  >
                    <FontAwesomeIcon icon={faTrash} className="text-xs" />
                  </IconButton>
                </div>
              ))}
            </div>
          </div>
        )}

        <TextField
          size="small"
          label="Noter (interne)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          fullWidth
          multiline
          minRows={2}
        />

        {error && (
          <div className="px-3 py-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-sm">
            {error}
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-3 px-5 py-3 border-t border-slate-200 bg-slate-50/40">
        {savedAt && Date.now() - savedAt < 4000 && (
          <span className="text-xs text-emerald-600">Gemt.</span>
        )}
        <button
          onClick={handleSave}
          disabled={busy}
          className="px-4 py-1.5 text-xs font-semibold text-white bg-success hover:brightness-95 disabled:bg-slate-300 disabled:hover:brightness-100 rounded-sm transition-all"
        >
          {busy ? "Gemmer…" : "Gem plan"}
        </button>
      </div>
    </div>
  );
}
